import {
	type EventMappings,
	type PageDetails,
	type PagePerformance,
	SessionStateType,
	Track,
	type WorkflowId,
} from '@1debit/chime-schemas/typescript_web_analytics';

import {
	type AppEventContext,
	type EventData,
	EventRouterService,
	EventType,
	type UserProperties,
} from './EventRouterService';
import { Logger } from './Logger';
import { SessionManager } from './SessionManager';
import { type Environment } from './utils/environments';

export type EventRouterHandlerConfig = {
	env: Environment;
	deviceId: string;
	applicationName?: string;
};

enum State {
	IDLE = 'IDLE',
	INITIALIZED = 'INITIALIZED',
}

export enum ValidationError {
	UNIQUE_ID_NOT_SNAKE_CASE = 'UNIQUE_ID_NOT_SNAKE_CASE',
	UNIQUE_ID_TOO_LONG = 'UNIQUE_ID_TOO_LONG',
	UNIQUE_ID_IN_ITEM_DETAILS_MISMATCH = 'UNIQUE_ID_IN_ITEM_DETAILS_MISMATCH',
	ITEM_DETAILS_TOO_MANY_KEYS = 'ITEM_DETAILS_TOO_MANY_KEYS',
	PAGE_ID_NOT_SNAKE_CASE = 'PAGE_ID_NOT_SNAKE_CASE',
	PAGE_ID_TOO_LONG = 'PAGE_ID_TOO_LONG',
	PAGE_DETAILS_TOO_MANY_KEYS = 'PAGE_DETAILS_TOO_MANY_KEYS',
	PAGE_ID_IN_PAGE_DETAILS_MISMATCH = 'PAGE_ID_IN_PAGE_DETAILS_MISMATCH',
}

const EVENT_ROUTER_TRACK_PATH = '/event-router/v2/track';
const MAX_UNIQUE_ID_LENGTH = 256;

type EventRecord = {
	event_index: number;
	event_id: string;
};

type InvalidEventRecord = EventRecord & {
	errors: ReadonlyArray<object>;
};

export type EventRouterResponse = {
	invalid_records: ReadonlyArray<InvalidEventRecord>;
	failed_records: ReadonlyArray<EventRecord>;
	successful_records: ReadonlyArray<EventRecord>;
	message: string;
};

export class EventRouterHandler {
	private config: EventRouterHandlerConfig;
	private state = State.IDLE;
	private service: EventRouterService;
	private sessionManager: SessionManager;
	private logger: Logger;

	constructor(config: EventRouterHandlerConfig) {
		this.config = config;
		this.logger = new Logger({
			applicationName: this.config.applicationName,
			env: this.config.env,
		});
		this.service = new EventRouterService(config.deviceId);
		this.sessionManager = new SessionManager({
			onNewSession: session => {
				this.trackEvent(Track.WebSessionStateChanged, {
					id: session.id,
					type: SessionStateType.SESSION_STATE_TYPE_START,
					event_ts: new Date(session.end_ts).toISOString(),
				});
			},
		});
		this.service.updateSessionId(this.getSessionId());
	}

	public identify(userId: number, userProperties?: UserProperties): void {
		this.service.setUserData(userId, userProperties);
	}

	public reset(): void {
		this.service.removeUserData();
	}

	public init(appEventContext?: AppEventContext): Promise<void> {
		this.state = State.INITIALIZED;
		this.service.updateContext(appEventContext);
		return this.sendData();
	}

	public getSessionId(): string {
		return this.sessionManager.getAndUpdateSession();
	}

	private logWarning(message?: unknown, ...optionalParams: unknown[]): void {
		if (this.config.env !== 'production') {
			console.warn(message, ...optionalParams);
		}
	}

	private async sendData(shouldRetry = true): Promise<void> {
		const shouldSendEvents = this.service.shouldSendEvents();

		if (!shouldSendEvents) {
			return;
		}

		const payload = this.service.createPayloadObject();
		this.service.clearEvents();

		try {
			const response = await fetch(EVENT_ROUTER_TRACK_PATH, {
				method: 'POST',
				body: JSON.stringify(payload),
				headers: {
					'Content-Type': 'application/json',
				},
				keepalive: true,
			});

			const statusCode = response.status.toString();
			if (statusCode === '206' && shouldRetry) {
				const responseBody: EventRouterResponse = await response.json();

				const events = new Map(payload.events.map(event => [event.event_id as string, event]));
				responseBody.failed_records.forEach(record => {
					const event = events.get(record.event_id);
					if (event) {
						this.service.addEventToRetry(event);
					}
				});
				return this.sendData(false);
			}

			if (statusCode.startsWith('5')) {
				if (shouldRetry) {
					this.logWarning(`Event router returned ${statusCode} status code. Retrying...`);
					payload.events.forEach(event => {
						this.service.addEventToRetry(event);
					});
					return this.sendData(false);
				} else {
					this.logWarning(`Event router returned ${statusCode} status code on a retry.`);
				}
			} else if (statusCode.startsWith('4')) {
				const responseBody = await response.json();
				this.logWarning(`Event router returned ${statusCode} status code.`, { responseBody });
			}
		} catch (error) {
			this.logWarning('Error sending data to event router', error);
		}
	}

	private isUniqueIdSnakeCase(uniqueId: string): boolean {
		const snakeCaseRegex = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
		return snakeCaseRegex.test(uniqueId);
	}

	private isUniqueIdLengthValid(uniqueId: string): boolean {
		return uniqueId.length <= MAX_UNIQUE_ID_LENGTH;
	}

	private validateEvent<EventName extends Track>(properties: EventMappings[EventName]): boolean {
		if ('unique_id' in properties && properties.unique_id) {
			const isSnakeCase = this.isUniqueIdSnakeCase(properties.unique_id);
			if (!isSnakeCase) {
				this.logWarning(`[Analytics], unique_id ${properties.unique_id} must be in snake_case`);
				this.logger.error('unique_id must be in snake_case', {
					unique_id: properties.unique_id,
					error: ValidationError.UNIQUE_ID_NOT_SNAKE_CASE,
				});
				return false;
			}

			const isLengthValid = this.isUniqueIdLengthValid(properties.unique_id);

			if (!isLengthValid) {
				this.logWarning(
					`[Analytics], unique_id ${properties.unique_id} must be less than ${MAX_UNIQUE_ID_LENGTH} characters`,
				);
				this.logger.error('unique_id is too long', {
					unique_id: properties.unique_id,
					error: ValidationError.UNIQUE_ID_TOO_LONG,
				});
				return false;
			}
		}

		if ('unique_id' in properties && 'item_details' in properties) {
			if (properties.item_details && !(properties.unique_id in properties.item_details)) {
				this.logWarning(`[Analytics], ${properties.unique_id} must be a valid key in EventRouterItemDetails`);
				this.logger.error('unique_id must be a valid key in EventRouterItemDetails', {
					unique_id: properties.unique_id,
					error: ValidationError.UNIQUE_ID_IN_ITEM_DETAILS_MISMATCH,
				});
				return false;
			}

			if (properties.item_details && Object.keys(properties.item_details).length > 1) {
				this.logWarning(`[Analytics], item_details must have exactly one key`);
				this.logger.error('item_details has too many keys', {
					unique_id: properties.unique_id,
					error: ValidationError.ITEM_DETAILS_TOO_MANY_KEYS,
				});
				return false;
			}
		}

		return true;
	}

	public trackEvent<EventName extends Track>(
		eventName: EventName,
		properties: EventMappings[EventName],
	): Promise<void> | undefined {
		// skip the requests in development because the proxy won't be running
		if (this.config.env === 'development' || typeof window === 'undefined') {
			return;
		}

		if (eventName !== Track.WebSessionStateChanged) {
			this.service.updateSessionId(this.getSessionId());
		}

		if (!this.validateEvent(properties)) {
			return;
		}

		const analyticsData: EventData = {
			name: eventName,
			properties,
			type: EventType.Track,
		};
		const wasEventAdded = this.service.createAndAddEvent(analyticsData);

		if (!wasEventAdded) {
			this.logWarning(
				'[Analytics], Event batch is full. That might mean that the Analytics class is not initialized correctly',
			);
			return;
		}

		if (this.state !== State.INITIALIZED) {
			return;
		}

		this.sendData();
	}

	private validatePageEvent(pageId: string, pageDetails?: PageDetails): boolean {
		const isSnakeCase = this.isUniqueIdSnakeCase(pageId);
		if (!isSnakeCase) {
			this.logWarning(`[Analytics], page_id ${pageId} must be in snake_case`);
			this.logger.error('page_id must be in snake_case', {
				page_id: pageId,
				error: ValidationError.PAGE_ID_NOT_SNAKE_CASE,
			});
			return false;
		}

		const isLengthValid = this.isUniqueIdLengthValid(pageId);
		if (!isLengthValid) {
			this.logWarning(`[Analytics], page_id ${pageId} must be less than ${MAX_UNIQUE_ID_LENGTH} characters`);
			this.logger.error('page_id is too long', {
				page_id: pageId,
				error: ValidationError.PAGE_ID_TOO_LONG,
			});
			return false;
		}

		if (pageDetails) {
			if (Object.keys(pageDetails).length > 1) {
				this.logWarning(`[Analytics], page_details must have exactly one key`);
				this.logger.error('page_details has too many keys', {
					page_id: pageId,
					error: ValidationError.PAGE_DETAILS_TOO_MANY_KEYS,
				});
				return false;
			}

			const pageDetailsKey = Object.keys(pageDetails)[0];

			if (pageDetailsKey !== pageId) {
				this.logWarning(`[Analytics], page_details must have the same key as page_id`);
				this.logger.error('page_details must have the same key as page_id', {
					page_id: pageId,
					error: ValidationError.PAGE_ID_IN_PAGE_DETAILS_MISMATCH,
				});
				return false;
			}
		}

		return true;
	}

	public trackPage(pageId: string, pageDetails?: PageDetails, pagePerformance?: PagePerformance): void {
		if (!this.validatePageEvent(pageId, pageDetails)) {
			return;
		}

		this.service.updatePageId(pageId);

		this.trackEvent(Track.WebPageViewed, {
			page_details: pageDetails,
			page_performance: pagePerformance,
		});
	}

	public startWorkflow(workflowId: WorkflowId) {
		this.service.addWorkflow(workflowId);
	}

	public endWorkflow(workflowId: WorkflowId) {
		const removed = this.service.removeWorkflow(workflowId);
		if (!removed) {
			this.logWarning(`[Analytics], Tried to end workflow ${workflowId} that hasn't been started`);
		}
	}
}
